import React, { FC, useContext } from 'react';
import injectSheet from 'react-jss';

import PagingLeftArrow from 'Assets/ui/pagingLeftArrow.svg';
import PagingRightArrow from 'Assets/ui/pagingRightArrow.svg';
import { calculateRange } from 'Components/Paging';
import { numberWithCommas } from 'Utils/numberWithCommas';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import { useRouter } from 'Utils/router';
import { Label } from '../Label';
import styleSheet from './styles';

interface Props {
  classes: any;
  totalRecords: number | string;
  recordsPerPage: number;
  page: number;
  i18nWordOF?: string;
  rangeDivider?: string;
  prev: any;
  next: any;
  position?: string;
  parent?: string;
  totalPages?: number;
}
const labels = (
  classes: any,
  page: number,
  recordsPerPage: number,
  totalRecords: number | string,
  i18nWordOF: string,
  rangeDivider: string
) => {
  const { start, end } = calculateRange({
    page,
    recordsPerPage,
    totalRecords,
  });

  return (
    <div className={classes.counters}>
      <div className={classes.flexItem}>
        <Label className={`${classes.label} ${classes.startLabel}`}>
          {numberWithCommas(start)}
        </Label>
      </div>
      <div className={classes.flexItem}>
        <Label className={classes.label}>{rangeDivider}</Label>
      </div>
      <div className={classes.flexItem}>
        <Label className={`${classes.label} ${classes.endLabel}`}>
          {numberWithCommas(end)}
        </Label>
      </div>
      <div className={classes.flexItem}>
        <Label className={classes.label}>{i18nWordOF}</Label>
      </div>
      <div className={classes.flexItem}>
        <Label className={`${classes.label} ${classes.totalRecordsLabel}`}>
          {numberWithCommas(
            (typeof totalRecords === 'number' && totalRecords) ||
              (typeof totalRecords === 'string' && parseInt(totalRecords, 10))
          )}
        </Label>
      </div>
    </div>
  );
};
const pagingNav = (classes: any, prev: any, next: any) => {
  return (
    <div className={classes.pagingNav}>
      <div className={classes.flexItem}>
        <button
          aria-label="previous page"
          className={`prev ${classes.arrowWrapper}`}
          onClick={prev}
          type="button"
        >
          <PagingLeftArrow
            className={`${classes.arrowCommon} ${classes.arrowPrevious}`}
          />
        </button>
      </div>
      <div className={`${classes.flexItem} ${classes.flexItemPaddLeft}`}>
        <button
          aria-label="next page"
          className={`next ${classes.arrowWrapper}`}
          onClick={next}
          type="button"
        >
          <PagingRightArrow className={classes.arrowCommon} />
        </button>
      </div>
    </div>
  );
};
const pagingPositionClasses = (classes: any, position?: string) => {
  // Capture the mangled classes, to allow a user to pass in a value of Left, Right or Center
  const positions = {
    left: `${classes.pageContainerLeft} ${classes.pagingContainerLeftPadding}`,
    center: `${classes.pageContainerCenter}`,
    right: `${classes.pageContainerRight} ${classes.pagingContainerRightPadding}`,
  };

  // set a default value
  const finalPosition =
    position && position.length ? position.toLocaleLowerCase() : 'center'; // Center is the default setting

  return positions[finalPosition];
};
const Paging: FC<Props> = (props: Props) => {
  const {
    classes,
    prev: prevClick,
    next: nextClick,
    position,
    page,
    recordsPerPage,
    totalRecords,
    parent = '',
    totalPages = 0,
    rangeDivider,
    i18nWordOF,
  } = props;

  // Determine class for outer div
  const pagingPosition = pagingPositionClasses(classes, position);
  const {
    helpers: { View },
    actions: { track },
  } = useContext(ProductAnalyticsContext);
  const { location } = useRouter();
  const trackPaginationClick = (direction: string) => {
    // Combined Analytics event tracking
    track('pagination_clicked', {
      direction,
      parent: `${View(location.pathname)} - ${parent}`,
      page_count: totalPages,
      current_page: page,
    });
  };
  const next = () => {
    trackPaginationClick('Next');
    nextClick();
  };
  const prev = () => {
    trackPaginationClick('Previous');
    prevClick();
  };
  return (
    <div
      className={`paging ${classes.pageContainer} ${pagingPosition}`}
      data-testid="classes.page"
    >
      <div>
        <div className={classes.flexContainer}>
          {labels(
            classes,
            page,
            recordsPerPage,
            totalRecords,
            i18nWordOF,
            rangeDivider
          )}
          {totalRecords > recordsPerPage && pagingNav(classes, prev, next)}
        </div>
      </div>
    </div>
  );
};

Paging.defaultProps = {
  position: 'center',
  parent: '',
  totalPages: 0,
  i18nWordOF: 'of',
  rangeDivider: '-',
};

export default injectSheet(styleSheet)(Paging);
